import { Autocomplete, DatePicker } from '@fullscreendigital/carrefour-dex-fe'
import { getCurrentDate, getPreviousWeek } from 'utils/helpers'
import { ActionButton } from 'components/core-ui/ActionButton'
import { MultivalueCell } from 'components/core-ui/MultivalueCell'
import { WasteCategoryReportTableItem } from '../interfaces'

const hasDocument = (documents: string[], type: string) =>
  documents.some(document => document === type)

export const getColumns = (selectDocumentForDownload: (is: number, type: string) => void) => [
  {
    title: 'Nr. Aviz',
    field: 'wanNumber',
    align: 'left',
    type: 'string',
    sticky: true
  },
  {
    title: 'Societate',
    field: 'legalEntity',
    align: 'left',
    type: 'string',
    sticky: true,
    disableSorting: true
  },
  {
    title: 'Locatie',
    field: 'storeName',
    align: 'left',
    type: 'string',
    sticky: true
  },
  {
    title: 'Data',
    field: 'creationDate',
    align: 'left',
    type: 'string',
    sticky: true
  },
  {
    title: 'Categorii',
    field: 'wasteCategories',
    align: 'left',
    type: 'string',
    collapsible: true,
    render: (row: WasteCategoryReportTableItem) => (
      <MultivalueCell value={row.wasteCategories} field="name" size={1} />
    ),
    disableSorting: true
  },
  {
    title: 'Destinatar',
    field: 'companyName',
    align: 'left',
    type: 'string',
    collapsible: true
  },
  {
    title: 'Total cant. predata (kg)',
    field: 'measuredWeight',
    align: 'left',
    type: 'string',
    collapsible: true,
    disableSorting: true
  },
  {
    title: 'Anexe transport',
    field: 'wasteReports',
    align: 'left',
    type: 'string',
    collapsible: true,
    disableSorting: true,
    render: (row: WasteCategoryReportTableItem) => (
      <MultivalueCell value={row.wasteReports} field="name" size={1} />
    )
  },
  {
    title: 'Aviz insotire marfa',
    field: 'documents[0]',
    align: 'left',
    type: 'string',
    collapsible: true,
    disableSorting: true,
    render: (row: WasteCategoryReportTableItem) => {
      const isEnabled = hasDocument(row.documents, 'ACCOMPANYING_NOTE')
      return (
        <ActionButton
          label="Download"
          disabled={!isEnabled}
          onClick={() => {
            if (isEnabled) selectDocumentForDownload(row.id, 'ACCOMPANYING_NOTE')
          }}
        />
      )
    }
  },
  {
    title: 'Doc. de miscare a SNCU',
    field: 'documents[1]',
    align: 'left',
    type: 'string',
    collapsible: true,
    disableSorting: true,
    render: (row: WasteCategoryReportTableItem) => {
      const isEnabled = hasDocument(row.documents, 'SNCU_MOVEMENT_DOCUMENT')
      return (
        <ActionButton
          label="Download"
          disabled={!isEnabled}
          onClick={() => {
            if (isEnabled) selectDocumentForDownload(row.id, 'SNCU_MOVEMENT_DOCUMENT')
          }}
        />
      )
    }
  },
  {
    title: 'Certificatul sanitar-veterinar (CSV)',
    field: 'documents[2]',
    align: 'left',
    type: 'string',
    collapsible: true,
    disableSorting: true,
    render: (row: WasteCategoryReportTableItem) => {
      const isEnabled = hasDocument(row.documents, 'VETERINARY_INSPECTION_CERTIFICATE')
      return (
        <ActionButton
          label="Download"
          disabled={!isEnabled}
          onClick={() => {
            if (isEnabled) selectDocumentForDownload(row.id, 'VETERINARY_INSPECTION_CERTIFICATE')
          }}
        />
      )
    }
  },
  {
    title: 'Form. de incarcare descarcare',
    field: 'documents[3]',
    align: 'left',
    type: 'string',
    collapsible: true,
    disableSorting: true,
    render: (row: WasteCategoryReportTableItem) => {
      const isEnabled = hasDocument(row.documents, 'LOAD_UNLOAD_FORM')
      return (
        <ActionButton
          label="Download"
          disabled={!isEnabled}
          onClick={() => {
            if (isEnabled) selectDocumentForDownload(row.id, 'LOAD_UNLOAD_FORM')
          }}
        />
      )
    }
  },
  {
    title: 'Declaratie de curatare si dezinfectare a vehiculului/containerului',
    field: 'documents[4]',
    align: 'left',
    type: 'string',
    collapsible: true,
    disableSorting: true,
    render: (row: WasteCategoryReportTableItem) => {
      const isEnabled = hasDocument(row.documents, 'VEHICLE_CLEANING_DECLARATION')
      return (
        <ActionButton
          label="Download"
          disabled={!isEnabled}
          onClick={() => {
            if (isEnabled) selectDocumentForDownload(row.id, 'VEHICLE_CLEANING_DECLARATION')
          }}
        />
      )
    }
  }
]

const currentDate = getCurrentDate()
const previousWeek = getPreviousWeek()

export const getFilters = (
  legalEntitiesOptions: AutocompleteOption[],
  shopOptions: AutocompleteOption[],
  companyOptions: AutocompleteOption[],
  categoryOptions: AutocompleteOption[]
) => [
  {
    name: 'legalEntity',
    value: '',
    filter: (
      <Autocomplete variant="outlined" label="Societate" options={legalEntitiesOptions || []} />
    ),
    type: 'autocomplete',
    label: 'Societate'
  },
  {
    name: 'storeIds',
    value: undefined,
    filter: (
      <Autocomplete variant="outlined" label="Magazine" options={shopOptions || []} multiple />
    ),
    type: 'autocomplete',
    label: 'Magazine'
  },
  {
    name: 'startDate',
    value: previousWeek,
    filter: (
      <DatePicker
        label="Data inceput"
        variant="outlined"
        value={previousWeek}
        onChange={() => {}}
      />
    ),
    type: 'date',
    label: 'Data inceput'
  },
  {
    name: 'endDate',
    value: currentDate,
    filter: (
      <DatePicker label="Data sfarsit" variant="outlined" value={currentDate} onChange={() => {}} />
    ),
    type: 'date',
    label: 'Data sfarsit'
  },
  {
    name: 'categories',
    value: undefined,
    filter: (
      <Autocomplete
        variant="outlined"
        label="Categorii deseuri"
        options={categoryOptions || []}
        multiple
      />
    ),
    type: 'autocomplete',
    label: 'Categorie deseuri'
  },
  {
    name: 'companyId',
    value: '',
    filter: <Autocomplete variant="outlined" label="Destinatar" options={companyOptions || []} />,
    type: 'autocomplete',
    label: 'Destinatar'
  }
]
